import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Film, MapPin, Users, Bookmark, Send, PlayCircle, ExternalLink,
  ArrowLeft, Sparkles, Landmark, Handshake, ShieldCheck,
} from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import ScoreBadge from "@/components/ScoreBadge";
import { api, apiUrl } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const stageLabels = {
  idea: "Idea", development: "Desarrollo", script: "Guion", preproduction: "Preproducción",
  shooting: "Rodaje", postproduction: "Posproducción", distribution: "Distribución", finished: "Terminado",
};

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [project, setProject] = useState(null);
  const [message, setMessage] = useState("");
  const [saved, setSaved] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/api/projects/${id}`);
        setProject(data);
      } catch (e) {
        toast.error(t("project.not_found"));
      }
    })();
  }, [id, t]);

  const save = async () => {
    if (!user) return nav("/login");
    try {
      await api.post("/api/saved", { target_type: "project", target_id: id });
      setSaved(true);
      toast.success(t("project.save_success"));
    } catch (e) {
      toast.error(t("project.save_failed"));
    }
  };

  const sendContact = async () => {
    if (!user) return nav("/login");
    if (!project?.owner_id) return;
    try {
      await api.post("/api/contacts", {
        to_user_id: project.owner_id,
        project_id: project.id,
        message: message || t("project.contact_default_msg", { title: project.title }),
      });
      toast.success(t("project.contact_sent"));
      setDialogOpen(false);
      setMessage("");
    } catch (e) {
      toast.error(t("project.contact_failed"));
    }
  };

  if (!project) {
    return (
      <div className="marketing-shell min-h-screen">
        <MarketingNav />
        <div className="mx-auto max-w-6xl px-6 py-20 text-white/60">{t("common.loading")}</div>
      </div>
    );
  }

  const cover = project.cover_url || "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=1600";
  const financing = Math.min(100, Math.round(project.financing_percent || 0));

  return (
    <div className="marketing-shell min-h-screen">
      <MarketingNav />
      <section className="relative">
        <div className="absolute inset-0">
          <img src={cover} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 hero-mask" />
        </div>
        <div className="relative mx-auto max-w-6xl px-6 pt-12 pb-16">
          <button onClick={() => nav(-1)} className="text-white/70 inline-flex items-center gap-2 hover:text-gold mb-8" data-testid="button-back">
            <ArrowLeft size={16} /> {t("project.back")}
          </button>
          <div className="flex flex-wrap gap-2 mb-4">
            {project.format && <span className="eyebrow bg-white/10 text-white px-2.5 py-1 rounded-full">{project.format}</span>}
            {project.stage && <span className="eyebrow bg-gold text-petroleum px-2.5 py-1 rounded-full">{stageLabels[project.stage] || project.stage}</span>}
            {project.origin_country && <span className="eyebrow bg-white/10 text-white px-2.5 py-1 rounded-full inline-flex items-center gap-1"><MapPin size={10} /> {project.origin_country}</span>}
          </div>
          <h1 className="font-display text-white text-5xl md:text-6xl tracking-tight max-w-3xl leading-tight">
            {project.title}
          </h1>
          {project.logline && <p className="mt-6 text-white/80 text-xl max-w-3xl italic">{project.logline}</p>}
          {project.owner_id && (
            <div className="mt-6 flex items-center gap-3">
              <span className="text-white/60 text-xs uppercase tracking-widest">{t("project.team_reputation")}</span>
              <ScoreBadge userId={project.owner_id} theme="dark" size="md" />
            </div>
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="button-request-contact" className="gold-cta rounded-full px-6 py-5 font-semibold">
                  <Send size={16} className="mr-2" /> {t("project.request_contact")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("project.contact_dialog_title")}</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-slate-500">{t("project.contact_dialog_desc")}</p>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t("project.contact_placeholder")}
                  data-testid="textarea-contact-message"
                  rows={5}
                />
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
                  <Button onClick={sendContact} data-testid="button-send-contact" className="bg-petroleum text-white hover:bg-petroleum/90">{t("common.send")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Button
              onClick={save}
              variant="outline"
              className="ghost-cta rounded-full px-6 py-5 font-semibold border-white/20 text-white hover:text-gold hover:bg-white/5"
              data-testid="button-save-project"
            >
              <Bookmark size={16} className="mr-2" fill={saved ? "currentColor" : "none"} />
              {saved ? t("project.saved") : t("project.save_project")}
            </Button>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="relative border-t border-white/5">
        <div className="mx-auto max-w-6xl px-6 py-16 grid lg:grid-cols-[2fr_1fr] gap-12">
          <div className="space-y-10">
            {project.synopsis_short && (
              <div>
                <p className="eyebrow text-gold mb-3">{t("project.synopsis")}</p>
                <p className="text-white/80 leading-relaxed text-lg">{project.synopsis_short}</p>
                {project.synopsis_long && <p className="text-white/70 leading-relaxed mt-4">{project.synopsis_long}</p>}
              </div>
            )}

            {(project.values?.length || project.themes?.length) ? (
              <div className="grid md:grid-cols-2 gap-6">
                {project.themes?.length ? (
                  <div className="glass-card rounded-2xl p-6">
                    <p className="eyebrow text-gold mb-3">{t("project.themes")}</p>
                    <div className="flex flex-wrap gap-2">
                      {project.themes.map((th) => <span key={th} className="text-sm px-3 py-1 rounded-full bg-white/5 text-white/80 border border-white/10">{th}</span>)}
                    </div>
                  </div>
                ) : null}
                {project.values?.length ? (
                  <div className="glass-card rounded-2xl p-6">
                    <p className="eyebrow text-gold mb-3">{t("project.values")}</p>
                    <div className="flex flex-wrap gap-2">
                      {project.values.map((v) => <span key={v} className="text-sm px-3 py-1 rounded-full bg-gold/15 text-gold border border-gold/30">{v}</span>)}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {(project.audience_primary || project.audience_countries?.length) && (
              <div>
                <p className="eyebrow text-gold mb-3">{t("project.audience")}</p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <InfoTile label={t("project.audience_profile")} value={project.audience_primary || "—"} />
                  <InfoTile label={t("project.audience_age")} value={project.audience_age_min && project.audience_age_max ? `${project.audience_age_min}–${project.audience_age_max}` : "—"} />
                  <InfoTile label={t("project.audience_territories")} value={(project.audience_countries || []).join(", ") || "—"} />
                </div>
              </div>
            )}

            {project.placements?.length ? (
              <div>
                <p className="eyebrow text-gold mb-3">{t("project.placements_title")}</p>
                <div className="space-y-3">
                  {project.placements.map((p) => (
                    <div key={p.id} className="glass-card rounded-2xl p-5">
                      <div className="flex items-center justify-between gap-4">
                        <h4 className="font-display text-xl text-white">{p.name}</h4>
                        <span className="text-xs uppercase tracking-widest text-gold">{p.placement_type}</span>
                      </div>
                      {p.description && <p className="text-white/70 text-sm mt-2">{p.description}</p>}
                      <div className="grid sm:grid-cols-3 gap-3 mt-4 text-xs text-white/60">
                        {p.product_category && <span>{t("project.category")}: <span className="text-white">{p.product_category}</span></span>}
                        {p.screen_duration && <span>{t("project.screen_time")}: <span className="text-white">{p.screen_duration}</span></span>}
                        {p.requested_value && <span>{t("project.value_label")}: <span className="text-gold">{p.currency} {p.requested_value.toLocaleString()}</span></span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {project.packages?.length ? (
              <div>
                <p className="eyebrow text-gold mb-3">{t("project.packages_title")}</p>
                <div className="grid md:grid-cols-2 gap-4">
                  {project.packages.map((p) => (
                    <div key={p.id} className="glass-card rounded-2xl p-6">
                      <p className="text-xs uppercase tracking-widest text-gold">{p.tier}</p>
                      <h4 className="font-display text-2xl text-white mt-1">{p.name}</h4>
                      {p.requested_value && <p className="text-gold text-lg mt-2">{p.currency} {p.requested_value.toLocaleString()}</p>}
                      {p.benefits?.length ? (
                        <ul className="mt-3 space-y-2">
                          {p.benefits.map((b) => <li key={b} className="text-sm text-white/70 flex gap-2"><Sparkles size={12} className="text-gold shrink-0 mt-1" /> {b}</li>)}
                        </ul>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {project.reel_url ? (
              <div>
                <p className="eyebrow text-gold mb-3">{t("project.media_title")}</p>
                <a href={project.reel_url} target="_blank" rel="noreferrer" className="glass-card rounded-2xl p-5 flex items-center gap-4 hover:border-gold/40 transition-colors">
                  <span className="inline-flex h-12 w-12 rounded-full bg-gold text-petroleum items-center justify-center"><PlayCircle size={20} /></span>
                  <div>
                    <p className="text-white">{t("project.watch_reel")}</p>
                    <p className="text-xs text-white/50 flex items-center gap-1">{project.reel_url} <ExternalLink size={10} /></p>
                  </div>
                </a>
              </div>
            ) : null}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            <div className="glass-card rounded-2xl p-6">
              <p className="eyebrow text-gold mb-4">{t("project.finance_state")}</p>
              <div className="text-3xl font-display text-white">{financing}<span className="text-lg text-white/60 ml-1">{t("project.financed")}</span></div>
              <div className="mt-3 h-2 rounded-full bg-white/10">
                <div className="h-2 rounded-full bg-gold" style={{ width: `${financing}%` }} />
              </div>
              {project.total_budget && (
                <div className="mt-4 text-sm text-white/70">{t("project.total_budget")} <span className="text-white">{project.currency} {project.total_budget.toLocaleString()}</span></div>
              )}
              {project.requested_on_cinemage && (
                <div className="mt-2 text-sm text-white/70">{t("project.requested")} <span className="text-gold">{project.currency} {project.requested_on_cinemage.toLocaleString()}</span></div>
              )}
            </div>

            <div className="glass-card rounded-2xl p-6 space-y-2">
              <p className="eyebrow text-gold mb-2">{t("project.modalities_sought")}</p>
              {project.seeks_placement && <div className="text-white/80 flex items-center gap-2"><Film size={14} className="text-gold" /> {t("project.mod_placement")}</div>}
              {project.seeks_sponsorship && <div className="text-white/80 flex items-center gap-2"><Handshake size={14} className="text-gold" /> {t("project.mod_sponsorship")}</div>}
              {project.seeks_cultural_funds && <div className="text-white/80 flex items-center gap-2"><Landmark size={14} className="text-gold" /> {t("project.mod_funds")}</div>}
            </div>

            <div className="glass-card rounded-2xl p-6">
              <p className="eyebrow text-gold mb-3">{t("project.compatible_sectors")}</p>
              <div className="flex flex-wrap gap-2">
                {(project.compatible_sectors || []).length === 0
                  ? <span className="text-white/50 text-sm">—</span>
                  : project.compatible_sectors.map((s) => <span key={s} className="text-xs px-3 py-1 rounded-full bg-white/5 text-white/80 border border-white/10 capitalize">{s}</span>)}
              </div>
            </div>

            <div className="glass-card rounded-2xl p-6">
              <p className="eyebrow text-gold mb-2 flex items-center gap-2"><ShieldCheck size={14} /> {t("project.ip_title")}</p>
              <p className="text-white/70 text-sm">{t("project.ip_desc")}</p>
            </div>
          </aside>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

function InfoTile({ label, value }) {
  return (
    <div className="glass-card rounded-2xl p-4">
      <p className="text-xs uppercase tracking-widest text-white/50">{label}</p>
      <p className="text-white mt-1">{value}</p>
    </div>
  );
}
